import React, { useState, useEffect, useMemo } from "react";
import { Download, Printer, Filter, Calendar } from "react-feather";
import { Table, Button, Dropdown, Menu, Popover } from "antd";
import { Line, Bar, Pie } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import axios from "axios";
import Swal from "sweetalert2";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import exportService from "../../utils/exportService";
import PrintReportPreview from "../../reports/PrintReportPreview";
import { renderDate } from "../../utils/dateFormatter";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend } from "chart.js";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";

const SalesPerformanceAndOccupancyReport = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({ date_wise_summary: [], room_type_summary: [] });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const business_date = localStorage.getItem("business_date");
    const start = business_date ? moment(business_date).tz("Asia/Kolkata").startOf("month") : moment().tz("Asia/Kolkata").startOf("month");
    const end = business_date ? moment(business_date).tz("Asia/Kolkata") : moment().tz("Asia/Kolkata");
    return { startDate: start, endDate: end };
  });
  const [companyId, setCompanyId] = useState(61);
  const [filterVisible, setFilterVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportFooter, setExportFooter] = useState([]);
  const [totalsForPageFooter, setTotalsForPageFooter] = useState([]);
  const [totalColumns, setTotalColumns] = useState([]);
  const [reportTitle, setReportTitle] = useState(t("sales_performance_occupancy_report"));
  const [filters, setFilters] = useState({ reportId: "" });
  const MIN_ROWS = 20;

const fetchReport = async () => {
  setLoading(true);
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(`${apiURL}/reports-daily/sales-performance-occupancy`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        start_date: dateRange.startDate.format("YYYY-MM-DD"),
        end_date: dateRange.endDate.format("YYYY-MM-DD"),
        companyid: companyId,
      },
    });
    console.log("API response:", response.data); // Debug log
    // Assume success if not explicitly false
    if (response.data.success !== false) {
      const report = response.data || {}; // Use response.data directly since it contains date_wise_summary
      setData({
        date_wise_summary: Array.isArray(report.date_wise_summary) ? report.date_wise_summary : [],
        room_type_summary: Array.isArray(report.room_type_summary) ? report.room_type_summary : [],
      });
    } else {
      Swal.fire({
        title: t("error"),
        text: t("failed_fetch_sales_report") || "Failed to fetch sales report",
        icon: "error",
      });
      setData({ date_wise_summary: [], room_type_summary: [] });
    }
  } catch (error) {
    console.error("Error fetching sales performance report:", error);
    Swal.fire({
      title: t("error"),
      text: t("failed_fetch_sales_report") || "Failed to fetch sales report",
      icon: "error",
    });
    setData({ date_wise_summary: [], room_type_summary: [] });
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchReport();
  }, [dateRange.startDate, dateRange.endDate, companyId]);

  const handleDateRangeChange = (event, picker) => {
    const newStartDate = moment(picker.startDate).tz("Asia/Kolkata").startOf("day");
    const newEndDate = moment(picker.endDate).tz("Asia/Kolkata").endOf("day");
    const businessDate = moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").endOf("day");

    if (!newStartDate.isValid() || !newEndDate.isValid()) {
      Swal.fire({ title: t("error"), text: t("invalid_date_selected"), icon: "error" });
      return;
    }
    if (newStartDate.isAfter(newEndDate)) {
      Swal.fire({ title: t("error"), text: t("start_date_after_end_date"), icon: "error" });
      return;
    }
    if (newStartDate.isAfter(businessDate) || newEndDate.isAfter(businessDate)) {
      Swal.fire({ title: t("error"), text: t("future_dates_not_allowed"), icon: "error" });
      return;
    }
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
  };

  const metrics = useMemo(() => {
    console.log("metrics data:", data); // Debug log
    const dateWise = Array.isArray(data.date_wise_summary) ? data.date_wise_summary : [];
    const totalRevenue = dateWise.reduce((sum, day) => sum + (day.revenue || 0), 0);
    const totalRoomsSold = dateWise.reduce((sum, day) => sum + (day.rooms_sold || 0), 0);
    const totalRoomsAvailable = dateWise.reduce((sum, day) => sum + (day.total_rooms || 0), 0);
    const totalDiscounts = dateWise.reduce((sum, day) => sum + (day.discount || 0), 0);
    const occupancyRate = totalRoomsAvailable > 0 ? (totalRoomsSold / totalRoomsAvailable) * 100 : 0;
    const adr = totalRoomsSold > 0 ? totalRevenue / totalRoomsSold : 0;
    const revpar = totalRoomsAvailable > 0 ? totalRevenue / totalRoomsAvailable : 0;

    return { totalRevenue, totalRoomsSold, totalRoomsAvailable, totalDiscounts, occupancyRate, adr, revpar };
  }, [data]);

  const handleExport = (format) => {
    if (!data.date_wise_summary?.length && format !== "print") {
      Swal.fire({
        title: t("error"),
        text: t("no_data_available_for_export"),
        icon: "error",
      });
      return;
    }

    const exportDataDaily = (data.date_wise_summary || []).map((day) => ({
      date: renderDate(day.date),
      rooms_sold: day.rooms_sold || 0,
      occupancy_rate: day.rooms_sold && day.total_rooms ? ((day.rooms_sold / day.total_rooms) * 100).toFixed(1) : "0.0",
      adr: day.revenue && day.rooms_sold ? (day.revenue / day.rooms_sold).toFixed(2) : "0.00",
      revpar: day.revenue && day.total_rooms ? (day.revenue / day.total_rooms).toFixed(2) : "0.00",
      total_revenue: (day.revenue || 0).toFixed(2),
      total_discounts: (day.discount || 0).toFixed(2),
    }));

    const exportDataRoomType = (data.room_type_summary || []).map((rt) => ({
      room_type: rt.type,
      rooms_sold: rt.rooms_sold || 0,
      occupancy_rate: rt.total_rooms > 0 ? ((rt.rooms_sold || 0) / rt.total_rooms * 100).toFixed(1) : "0.0",
      total_revenue: (rt.revenue || 0).toFixed(2),
      average_discount: rt.rooms_sold > 0 ? ((rt.discount || 0) / rt.rooms_sold).toFixed(2) : "0.00",
    }));

    const exportHeadersDaily = [
      { key: "date", label: t("date") },
      { key: "rooms_sold", label: t("rooms_sold") },
      { key: "occupancy_rate", label: t("occupancy_rate") },
      { key: "adr", label: t("adr") },
      { key: "revpar", label: t("revpar") },
      { key: "total_revenue", label: t("total_revenue") },
      { key: "total_discounts", label: t("total_discounts") },
    ];

    const exportHeadersRoomType = [
      { key: "room_type", label: t("room_type") },
      { key: "rooms_sold", label: t("rooms_sold") },
      { key: "occupancy_rate", label: t("occupancy_rate") },
      { key: "total_revenue", label: t("total_revenue") },
      { key: "average_discount", label: t("average_discount") },
    ];

    const exportFooterDaily = [
      { key: "total", value: t("total") },
      { key: "rooms_sold", value: metrics.totalRoomsSold },
      { key: "occupancy_rate", value: `${metrics.occupancyRate.toFixed(1)}%` },
      { key: "adr", value: `₹${metrics.adr.toFixed(2)}` },
      { key: "revpar", value: `₹${metrics.revpar.toFixed(2)}` },
      { key: "total_revenue", value: `₹${metrics.totalRevenue.toFixed(2)}` },
      { key: "total_discounts", value: `₹${metrics.totalDiscounts.toFixed(2)}` },
    ];

    const exportFooterRoomType = [
      { key: "total", value: t("total") },
      { key: "rooms_sold", value: (data.room_type_summary || []).reduce((sum, rt) => sum + (rt.rooms_sold || 0), 0) },
      {
        key: "occupancy_rate",
        value: `${
          (
            ((data.room_type_summary || []).reduce((sum, rt) => sum + (rt.rooms_sold || 0), 0) /
              ((data.room_type_summary || []).reduce((sum, rt) => sum + (rt.total_rooms || 0), 0) || 1)) *
            100
          ).toFixed(1) || "0.0"
        }%`,
      },
      {
        key: "total_revenue",
        value: `₹${(data.room_type_summary || []).reduce((sum, rt) => sum + (rt.revenue || 0), 0).toFixed(2)}`,
      },
      {
        key: "average_discount",
        value: `₹${(
          (data.room_type_summary || []).reduce((sum, rt) => sum + (rt.discount || 0), 0) /
          ((data.room_type_summary || []).reduce((sum, rt) => sum + (rt.rooms_sold || 0), 0) || 1)
        ).toFixed(2)}`,
      },
    ];

    const filename = `sales_performance_occupancy_report_${dateRange.startDate.format("YYYY-MM-DD")}_${dateRange.endDate.format("YYYY-MM-DD")}`;

    if (format === "xls") {
      exportService.exportToXLS(
        [...exportDataDaily, { divider: "Room Type Summary" }, ...exportDataRoomType],
        [...exportHeadersDaily, ...exportHeadersRoomType],
        `${filename}.xlsx`,
        reportTitle
      );
    } else if (format === "csv") {
      exportService.exportToCSV(
        [...exportDataDaily, { divider: "Room Type Summary" }, ...exportDataRoomType],
        [...exportHeadersDaily, ...exportHeadersRoomType],
        `${filename}.csv`
      );
    } else if (format === "pdf") {
      exportService.exportToPDF(
        [...exportDataDaily, { divider: "Room Type Summary" }, ...exportDataRoomType],
        [...exportHeadersDaily, ...exportHeadersRoomType],
        reportTitle,
        `${filename}.pdf`,
        false,
        true,
        [...exportFooterDaily, ...exportFooterRoomType],
        [...exportFooterDaily, ...exportFooterRoomType]
      );
    } else if (format === "preview") {
      setExportData([...exportDataDaily, { divider: "Room Type Summary" }, ...exportDataRoomType]);
      setExportHeaders([...exportHeadersDaily, ...exportHeadersRoomType]);
      setExportFooter([...exportFooterDaily, ...exportFooterRoomType]);
      setTotalsForPageFooter([...exportFooterDaily, ...exportFooterRoomType]);
      setTotalColumns(["rooms_sold", "occupancy_rate", "adr", "revpar", "total_revenue", "total_discounts", "average_discount"]);
      setPreviewVisible(true);
    } else if (format === "print") {
      window.print();
    }
  };

  const filterContent = (
    <div style={{ width: "400px", padding: "10px" }}>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("report_id")}</label>
        <input
          type="text"
          className="form-control"
          value={filters.reportId}
          onChange={(e) => setFilters({ ...filters, reportId: e.target.value })}
          placeholder={t("search_report_id")}
        />
      </div>
    </div>
  );

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={() => handleExport("xls")}>{t("export_xls")}</Menu.Item>
      <Menu.Item key="csv" onClick={() => handleExport("csv")}>{t("export_csv")}</Menu.Item>
      <Menu.Item key="preview" onClick={() => handleExport("preview")}>{t("preview")}</Menu.Item>
    </Menu>
  );

  const activityStats = useMemo(
    () => [
      { label: t("total_revenue"), value: `₹${metrics.totalRevenue.toFixed(2)}`, color: "#28a745" },
      { label: t("occupancy_rate"), value: `${metrics.occupancyRate.toFixed(1)}%`, color: "#007bff" },
      { label: t("adr"), value: `₹${metrics.adr.toFixed(2)}`, color: "#17a2b8" },
      { label: t("revpar"), value: `₹${metrics.revpar.toFixed(2)}`, color: "#6f42c1" },
      { label: t("total_discounts"), value: `₹${metrics.totalDiscounts.toFixed(2)}`, color: "#dc3545" },
    ],
    [metrics, t]
  );

  const lineChartData = useMemo(() => ({
    labels: (data.date_wise_summary || []).map((day) => moment(day.date).format("DD MMM")),
    datasets: [
      {
        label: t("revenue"),
        data: (data.date_wise_summary || []).map((day) => day.revenue || 0),
        borderColor: "#28a745",
        backgroundColor: "rgba(40, 167, 69, 0.2)",
        yAxisID: "y",
      },
      {
        label: t("occupancy_rate"),
        data: (data.date_wise_summary || []).map((day) => (day.rooms_sold && day.total_rooms ? (day.rooms_sold / day.total_rooms) * 100 : 0)),
        borderColor: "#007bff",
        backgroundColor: "rgba(0, 123, 255, 0.2)",
        yAxisID: "y1",
      },
    ],
  }), [data, t]);

  const lineChartOptions = {
    responsive: true,
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: { display: true, text: t("revenue") },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: { display: true, text: t("occupancy_rate") },
        grid: { drawOnChartArea: false },
      },
    },
  };

  const barChartData = useMemo(() => ({
    labels: (data.room_type_summary || []).map((rt) => rt.type),
    datasets: [
      {
        label: t("revenue_by_room_type"),
        data: (data.room_type_summary || []).map((rt) => rt.revenue || 0),
        backgroundColor: [
          "#007bff",
          "#28a745",
          "#dc3545",
          "#ffc107",
          "#6f42c1",
          "#17a2b8",
          "#fd7e14",
          "#20c997",
          "#6610f2",
          "#e83e8c",
        ],
      },
    ],
  }), [data, t]);

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: t("revenue") },
      },
    },
  };

  const dailyBreakdownData = useMemo(() => {
    const dailyData = (data.date_wise_summary || []).map((day) => ({
      key: day.date,
      date: day.date,
      rooms_sold: day.rooms_sold || 0,
      total_rooms: day.total_rooms || 0,
      revenue: day.revenue || 0,
      discount: day.discount || 0,
    }));
    const paddedData = [...dailyData];
    for (let i = dailyData.length; i < MIN_ROWS; i++) {
      paddedData.push({
        key: `empty-daily-${i}`,
        date: "",
        rooms_sold: "",
        total_rooms: "",
        revenue: "",
        discount: "",
      });
    }
    return paddedData;
  }, [data]);

  const roomTypeData = useMemo(() => {
    const roomData = (data.room_type_summary || []).map((rt) => ({
      key: rt.type,
      room_type: rt.type,
      rooms_sold: rt.rooms_sold || 0,
      total_rooms: rt.total_rooms || 0,
      total_revenue: rt.revenue || 0,
      average_discount: rt.rooms_sold > 0 ? (rt.discount || 0) / rt.rooms_sold : 0,
      occupancy_rate: rt.total_rooms > 0 ? ((rt.rooms_sold || 0) / rt.total_rooms) * 100 : 0,
    }));
    const paddedData = [...roomData];
    for (let i = roomData.length; i < MIN_ROWS; i++) {
      paddedData.push({
        key: `empty-room-type-${i}`,
        room_type: "",
        rooms_sold: "",
        total_rooms: "",
        total_revenue: "",
        average_discount: "",
        occupancy_rate: "",
      });
    }
    return paddedData;
  }, [data]);

  const pieChartData = useMemo(() => {
    const sources = [...new Set((data.date_wise_summary || []).flatMap((day) => day.booking_source_breakdown?.map((bs) => bs.source) || []))];
    return {
      labels: sources,
      datasets: [
        {
          label: t("revenue_by_booking_source"),
          data: sources.map((source) =>
            (data.date_wise_summary || []).reduce((sum, day) => sum + (day.booking_source_breakdown?.find((bs) => bs.source === source)?.revenue || 0), 0)
          ),
          backgroundColor: ["#007bff", "#28a745", "#dc3545", "#ffc107"],
        },
      ],
    };
  }, [data, t]);

  const businessDate = moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").endOf("day");

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="sales-performance-report-container">
          <div className="page-header page-top-icons px-3 py-2 bg-white border rounded">
          <div className="row align-items-center flex-nowrap">
            <div className="col-auto d-flex align-items-center gap-3">
              <h2
                className="room-title mb-0"
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  width: "500px", // Increased width for more space
                  borderBottom: "2px solid #007bff",
                  paddingBottom: "4px",
                }}
              >
                {t("sales_performance_occupancy_report")}
              </h2>
              <div style={{ marginLeft: "auto" }}> {/* Push DateRangePicker to the right */}
                <DateRangePicker
                  key={`${dateRange.startDate.format("YYYY-MM-DD")}-${dateRange.endDate.format("YYYY-MM-DD")}`}
                  initialSettings={{
                    startDate: dateRange.startDate.toDate(),
                    endDate: dateRange.endDate.toDate(),
                    maxDate: businessDate.toDate(),
                    ranges: {
                      Today: [
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").startOf("day").toDate(),
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").endOf("day").toDate(),
                      ],
                      Yesterday: [
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "days").startOf("day").toDate(),
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "days").endOf("day").toDate(),
                      ],
                      "This Week": [
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").startOf("week").toDate(),
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").endOf("week").toDate(),
                      ],
                      "Last Week": [
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "week").startOf("week").toDate(),
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "week").endOf("week").toDate(),
                      ],
                      "This Month": [
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").startOf("month").toDate(),
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").endOf("month").toDate(),
                      ],
                      "Last Month": [
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "month").startOf("month").toDate(),
                        moment(localStorage.getItem("business_date")).tz("Asia/Kolkata").subtract(1, "month").endOf("month").toDate(),
                      ],
                    },
                    locale: { format: "DD MMM YYYY", applyLabel: "Apply", cancelLabel: "Cancel" },
                    alwaysShowCalendars: true,
                    autoApply: false,
                    linkedCalendars: false,
                  }}
                  onApply={handleDateRangeChange}
                >
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control text-center fw-bold"
                      value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                      style={{
                        height: "38px",
                        fontSize: "15px",
                        borderRight: "none",
                        backgroundColor: "#ffffff",
                        width: "220px",
                      }}
                      readOnly
                    />
                    <div className="input-group-append">
                      <span
                        className="input-group-text"
                        style={{
                          backgroundColor: "#fff",
                          borderLeft: "none",
                          borderRadius: "0 4px 4px 0",
                          cursor: "pointer",
                          height: "38px",
                        }}
                      >
                        <Calendar size={16} style={{ color: "#495057" }} />
                      </span>
                    </div>
                  </div>
                </DateRangePicker>
              </div>
            </div>
            <div className="col-auto ms-auto d-flex align-items-center gap-3">
              <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                <Button type="primary" className="d-flex align-items-center" style={{ height: "38px" }}>
                  <Download size={16} className="me-2" /> {t("download")}
                </Button>
              </Dropdown>
            </div>
          </div>
          </div>

          <div className="row g-4 mb-4" style={{ padding: "5px 10px" }}>
            {activityStats.map((stat, index) => (
              <div
                key={index}
                className="col-md-2 col-6 stats-box"
                style={{ margin: "0 15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", padding: "10px", position: "relative" }}
              >
                <div
                  className="stats-line"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    backgroundColor: stat.color,
                    borderRadius: "4px 0 0 4px",
                  }}
                ></div>
                <div className="stats-content" style={{ paddingLeft: "10px" }}>
                  <h2 style={{ fontSize: "1.5rem", margin: 0, fontFamily: "'Roboto', sans-serif" }}>{stat.value}</h2>
                  <p style={{ margin: 0, color: "#666", fontFamily: "'Roboto', sans-serif" }}>{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <div
                className="card"
                style={{
                  borderRadius: "0",
                  borderRight: "none",
                  overflowX: "auto",
                  border: "1px solid #dee2e6",
                  borderBottom: "none",
                }}
              >
                <div
                  className="card-header"
                  style={{
                    backgroundColor: "#fff",
                    padding: "12px 16px",
                    borderBottom: "none",
                    position: "relative",
                  }}
                >
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#333",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {t("revenue_occupancy_trends")}
                  </h5>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "0",
                      left: "0",
                      width: "100%",
                      height: "4px",
                      backgroundColor: "#28a745",
                    }}
                  />
                </div>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (data.date_wise_summary || []).length > 0 ? (
                    <Line data={lineChartData} options={lineChartOptions} />
                  ) : (
                    <div className="text-center my-5" style={{ color: "#666", fontSize: "16px" }}>
                      <p>{t("no_data_available")}</p>
                      <p>{t("please_select_a_different_date_range")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <div className="col-md-6">
                  <div
                    className="card"
                    style={{
                      borderRadius: "0",
                      overflowX: "auto",
                      border: "1px solid #dee2e6",
                      borderBottom: "none",
                    }}
                  >
                    <div
                      className="card-header"
                      style={{
                        backgroundColor: "#fff",
                        padding: "12px 16px",
                        borderBottom: "none",
                        position: "relative",
                      }}
                    >
                      <h5
                        className="mb-0 d-flex align-items-center"
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#333",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("revenue_by_room_type")}
                      </h5>
                      <div
                        style={{
                          position: "absolute",
                          bottom: "0",
                          left: "0",
                          width: "100%",
                          height: "4px",
                          backgroundColor: "#007bff",
                        }}
                      />
                    </div>
                    <div className="card-body p-0" style={{ height: "300px" }}>
                      {loading ? (
                        <div className="text-center my-5">
                          <div className="loader">
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                          </div>
                        </div>
                      ) : (data.room_type_summary || []).length > 0 ? (
                        <Bar data={barChartData} options={barChartOptions} />
                      ) : (
                        <div className="text-center my-5" style={{ color: "#666", fontSize: "16px" }}>
                          <p>{t("no_data_available")}</p>
                          <p>{t("please_select_a_different_date_range")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div
                    className="card"
                    style={{
                      borderRadius: "0",
                      overflowX: "auto",
                      border: "1px solid #dee2e6",
                      borderBottom: "none",
                    }}
                  >
                    <div
                      className="card-header"
                      style={{
                        backgroundColor: "#fff",
                        padding: "12px 16px",
                        borderBottom: "none",
                        position: "relative",
                      }}
                    >
                      <h5
                        className="mb-0 d-flex align-items-center"
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#333",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("booking_source_distribution")}
                      </h5>
                      <div
                        style={{
                          position: "absolute",
                          bottom: "0",
                          left: "0",
                          width: "100%",
                          height: "4px",
                          backgroundColor: "#dc3545",
                        }}
                      />
                    </div>
                    <div className="card-body p-0">
                      {loading ? (
                        <div className="text-center my-5">
                          <div className="loader">
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                          </div>
                        </div>
                      ) : (data.date_wise_summary || []).length > 0 ? (
                        <Pie data={pieChartData} options={{ responsive: true }} />
                      ) : (
                        <div className="text-center my-5" style={{ color: "#666", fontSize: "16px" }}>
                          <p>{t("no_data_available")}</p>
                          <p>{t("please_select_a_different_date_range")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-0">
            <div className="col-md-6">
              <div
                className="card"
                style={{
                  borderRadius: "0",
                  borderRight: "none",
                  overflowX: "auto",
                  border: "1px solid #dee2e6",
                  borderBottom: "none",
                }}
              >
                <div
                  className="card-header"
                  style={{
                    backgroundColor: "#fff",
                    padding: "12px 16px",
                    borderBottom: "none",
                    position: "relative",
                  }}
                >
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#333",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {t("daily_breakdown")} (Total Revenue: ₹{metrics.totalRevenue.toFixed(2)})
                  </h5>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "0",
                      left: "0",
                      width: "100%",
                      height: "4px",
                      backgroundColor: "#28a745",
                    }}
                  />
                </div>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (data.date_wise_summary || []).length > 0 ? (
                    <Table
                      dataSource={dailyBreakdownData}
                      columns={[
                        {
                          title: t("date"),
                          dataIndex: "date",
                          key: "date",
                          render: (text) => (text ? renderDate(text) : ""),
                          sorter: (a, b) => (a.date ? moment(a.date).unix() : 0) - (b.date ? moment(b.date).unix() : 0),
                        },
                        {
                          title: t("rooms_sold"),
                          dataIndex: "rooms_sold",
                          key: "rooms_sold",
                          sorter: (a, b) => (a.rooms_sold || 0) - (b.rooms_sold || 0),
                        },
                        {
                          title: t("occupancy_rate"),
                          key: "occupancy_rate",
                          render: (record) => (record.rooms_sold && record.total_rooms ? ((record.rooms_sold / record.total_rooms) * 100).toFixed(1) : ""),
                          sorter: (a, b) =>
                            (a.rooms_sold && a.total_rooms ? (a.rooms_sold / a.total_rooms) * 100 : 0) -
                            (b.rooms_sold && b.total_rooms ? (b.rooms_sold / b.total_rooms) * 100 : 0),
                        },
                        {
                          title: t("adr"),
                          key: "adr",
                          render: (record) => (record.revenue && record.rooms_sold ? (record.revenue / record.rooms_sold).toFixed(2) : ""),
                          sorter: (a, b) => (a.revenue && a.rooms_sold ? a.revenue / a.rooms_sold : 0) - (b.revenue && b.rooms_sold ? b.revenue / b.rooms_sold : 0),
                        },
                        {
                          title: t("revpar"),
                          key: "revpar",
                          render: (record) => (record.revenue && record.total_rooms ? (record.revenue / record.total_rooms).toFixed(2) : ""),
                          sorter: (a, b) => (a.revenue && a.total_rooms ? a.revenue / a.total_rooms : 0) - (b.revenue && b.total_rooms ? b.revenue / b.total_rooms : 0),
                        },
                        {
                          title: t("total_revenue"),
                          dataIndex: "revenue",
                          key: "revenue",
                          render: (text) => (text ? text.toFixed(2) : ""),
                          sorter: (a, b) => (a.revenue || 0) - (b.revenue || 0),
                        },
                        {
                          title: t("total_discounts"),
                          dataIndex: "discount",
                          key: "discount",
                          render: (text) => (text ? text.toFixed(2) : ""),
                          sorter: (a, b) => (a.discount || 0) - (b.discount || 0),
                        },
                      ]}
                      rowKey={(record) => record.key}
                      bordered
                      pagination={false}
                      className="daybook-table"
                      style={{ margin: 0 }}
                      summary={() => (
                        <Table.Summary.Row style={{ backgroundColor: "#f8f9fa" }}>
                          <Table.Summary.Cell colSpan={5} style={{ fontWeight: 600, fontSize: "14px" }}>
                            {t("total")}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell style={{ fontWeight: 600, fontSize: "14px" }}>
                            ₹{metrics.totalRevenue.toFixed(2)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell style={{ fontWeight: 600, fontSize: "14px" }}>
                            ₹{metrics.totalDiscounts.toFixed(2)}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  ) : (
                    <div className="text-center my-5" style={{ color: "#666", fontSize: "16px" }}>
                      <p>{t("no_data_available")}</p>
                      <p>{t("please_select_a_different_date_range")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div
                className="card"
                style={{
                  borderRadius: "0",
                  overflowX: "auto",
                  border: "1px solid #dee2e6",
                  borderBottom: "none",
                }}
              >
                <div
                  className="card-header"
                  style={{
                    backgroundColor: "#fff",
                    padding: "12px 16px",
                    borderBottom: "none",
                    position: "relative",
                  }}
                >
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#333",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {t("room_type_performance")} (Total Revenue: ₹{metrics.totalRevenue.toFixed(2)})
                  </h5>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "0",
                      left: "0",
                      width: "100%",
                      height: "4px",
                      backgroundColor: "#007bff",
                    }}
                  />
                </div>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (data.room_type_summary || []).length > 0 ? (
                    <Table
                      dataSource={roomTypeData}
                      columns={[
                        {
                          title: t("room_type"),
                          dataIndex: "room_type",
                          key: "room_type",
                        },
                        {
                          title: t("rooms_sold"),
                          dataIndex: "rooms_sold",
                          key: "rooms_sold",
                          sorter: (a, b) => (a.rooms_sold || 0) - (b.rooms_sold || 0),
                        },
                        {
                          title: t("occupancy_rate"),
                          dataIndex: "occupancy_rate",
                          key: "occupancy_rate",
                          render: (text) => (text ? text.toFixed(1) : ""),
                          sorter: (a, b) => (a.occupancy_rate || 0) - (b.occupancy_rate || 0),
                        },
                        {
                          title: t("total_revenue"),
                          dataIndex: "total_revenue",
                          key: "total_revenue",
                          render: (text) => (text ? text.toFixed(2) : ""),
                          sorter: (a, b) => (a.total_revenue || 0) - (b.total_revenue || 0),
                        },
                        {
                          title: t("average_discount"),
                          dataIndex: "average_discount",
                          key: "average_discount",
                          render: (text) => (text ? text.toFixed(2) : ""),
                          sorter: (a, b) => (a.average_discount || 0) - (b.average_discount || 0),
                        },
                      ]}
                      rowKey={(record) => record.key}
                      bordered
                      pagination={false}
                      className="daybook-table"
                      style={{ margin: 0 }}
                      summary={() => (
                        <Table.Summary.Row style={{ backgroundColor: "#f8f9fa" }}>
                          <Table.Summary.Cell colSpan={3} style={{ fontWeight: 600, fontSize: "14px" }}>
                            {t("total")}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell style={{ fontWeight: 600, fontSize: "14px" }}>
                            ₹{metrics.totalRevenue.toFixed(2)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell style={{ fontWeight: 600, fontSize: "14px" }}>
                            ₹{(metrics.totalDiscounts / (metrics.totalRoomsSold || 1)).toFixed(2)}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  ) : (
                    <div className="text-center my-5" style={{ color: "#666", fontSize: "16px" }}>
                      <p>{t("no_data_available")}</p>
                      <p>{t("please_select_a_different_date_range")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <PrintReportPreview
            visible={previewVisible}
            onClose={() => setPreviewVisible(false)}
            reportTitle={reportTitle}
            propertyName=""
            data={exportData}
            headers={exportHeaders}
            footerData={totalsForPageFooter}
            totalColumns={totalColumns}
            apiEndpoint="transactions/generate-pdf"
            apiUrl={apiURL}
          />
        </div>
      </div>
    </div>
  );
};

export default SalesPerformanceAndOccupancyReport;